<template>
  <div class="custom-surveys-configuration-wrapper">
    <div class="body">
      <div class="content">
        <PageLayout>
          <template slot="header">
            <div>
              <div class="d-flex m-3 mx-lg-0">
                <Breadcrumb :list="breadcrumbList" />
              </div>
              <ReportsNavbar
                :canSeeSettings="summary.isOwner || isAdmin"
                :hasFeedbacks="summary.hasFeedbacks"
              />
            </div>
          </template>
          <template slot="main">
            <PageLayout class="custom-surveys-edit-page" :hasFullHeight="true">
              <template slot="header">
                <div class="header-wrap">
                  <EditMainNavbar
                    :activeTab="activeTab"
                    :navDisabled="
                      status === 'closed' || !$can('create', 'customSurvey')
                    "
                    @changeTab="changeTab"
                  />
                  <b-card v-if="activeTab === 0" class="survey-card mt-4">
                    <b-row>
                      <b-col cols="12" xl="6">
                        <b-form-group>
                          <b-input-group>
                            <b-form-input
                              class="readonly-input"
                              :value="cycle ? cycle : '--'"
                              readonly
                            >
                            </b-form-input>
                            <label class="custom-input-label">
                              {{ labels.settings.cycle }}
                            </label>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                      <b-col cols="12" xl="6">
                        <b-form-group>
                          <b-form-select
                            class="readonly-input custom-survey-status"
                            :class="status"
                            :value="status"
                            disabled
                          >
                            <option :value="status">
                              {{ labels.list.status[status] }}
                            </option>
                          </b-form-select>
                          <label class="custom-input-label">
                            {{ labels.list.status.label }}
                          </label>
                        </b-form-group>
                      </b-col>
                    </b-row>
                    <b-row>
                      <b-col cols="12" xl="6">
                        <b-form-group>
                          <b-input-group>
                            <b-form-input
                              class="readonly-input"
                              :value="
                                nextDeliveySurveyDate && status !== 'closed'
                                  ? $moment(nextDeliveySurveyDate).format(
                                      'DD/MM/YYYY - HH:00'
                                    )
                                  : '--'
                              "
                              readonly
                            >
                            </b-form-input>
                            <label class="custom-input-label">
                              {{ labels.settings.nextDeliveySurveyDate }}
                            </label>
                            <template v-slot:append>
                              <b-button
                                v-if="
                                  $can('create', 'customSurvey') &&
                                    ((['active', 'scheduled'].includes(
                                      status
                                    ) &&
                                      period !== 'single-shot') ||
                                      (status === 'scheduled' &&
                                        period === 'single-shot'))
                                "
                                :title="labels.settings.editNextDelivery"
                                class="apply-form-group-btn"
                                variant="link"
                                @click="openChangeModal(true)"
                              >
                                <i class="material-icons" v-if="!isLoading">
                                  calendar_today
                                </i>
                                <i class="mdi mdi-loading mdi-spin" v-else></i>
                              </b-button>
                            </template>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                      <b-col cols="12" xl="6">
                        <b-form-group>
                          <b-input-group>
                            <b-form-input
                              v-if="status === 'closed'"
                              class="readonly-input"
                              :value="
                                statusUpdate.date
                                  ? $moment(statusUpdate.date).format(
                                      'DD/MM/YYYY'
                                    )
                                  : '--'
                              "
                              readonly
                            >
                            </b-form-input>
                            <b-form-input
                              v-else
                              class="readonly-input"
                              :value="
                                launchConfiguration.endDate
                                  ? $moment(launchConfiguration.endDate).format(
                                      'DD/MM/YYYY'
                                    )
                                  : '--'
                              "
                              readonly
                            >
                            </b-form-input>
                            <label class="custom-input-label">
                              {{ labels.settings.closedSurveyDate }}
                            </label>
                            <template v-slot:append>
                              <b-button
                                v-if="
                                  ['active', 'scheduled'].includes(status) &&
                                    $can('create', 'customSurvey')
                                "
                                :title="labels.settings.editClose"
                                class="apply-form-group-btn"
                                variant="link"
                                @click="openChangeModal(false)"
                              >
                                <i class="material-icons" v-if="!isLoading">
                                  calendar_today
                                </i>
                                <i class="mdi mdi-loading mdi-spin" v-else></i>
                              </b-button>
                            </template>
                          </b-input-group>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-card>
                  <LanguageNavbar
                    v-if="activeTab !== 1"
                    class="mt-4"
                    :languages="languages"
                    :availableLanguages="availableLanguages"
                    :selectedLanguage="selectedLanguage"
                    @selectLanguage="selectLanguage"
                  />
                </div>
              </template>
              <template slot="main">
                <div class="custom-surveys-create-container">
                  <SummaryTab
                    v-if="activeTab === 0"
                    :languages="languages"
                    :availableLanguages="availableLanguages"
                    :selectedLanguage="selectedLanguage"
                    :title="title"
                    :description="description"
                    :period="period"
                    :questionsDistribution="questionsDistribution"
                    :questionsQuantity="questionsQuantity"
                    :enableFeedbacks="enableFeedbacks"
                    :mandatoryQuestions="mandatoryQuestions"
                    :questions="questions"
                    :categories="categories"
                    :sendToCompany="surveyPublic.sendToCompany"
                    :groupsWithHierarchy="surveyPublic.groupsWithHierarchy"
                    :groupsOnly="surveyPublic.groupsOnly"
                    :users="surveyPublic.users"
                    :managersPermission="surveyAccess.managersPermission"
                    :surveyAccessUsers="surveyAccess.users"
                    :creator="summary.creator"
                  />
                  <ConfigurationsTab
                    class="mt-4"
                    v-if="activeTab === 1"
                    :period="period"
                    :languages="languages"
                    :availableLanguages="availableLanguages"
                    :questionsDistribution="questionsDistribution"
                    :questionsQuantity="questionsQuantity"
                    :enableFeedbacks="enableFeedbacks"
                    :mandatoryQuestions="mandatoryQuestions"
                    :sendToCompany="surveyPublic.sendToCompany"
                    :groupsWithHierarchy="surveyPublic.groupsWithHierarchy"
                    :groupsOnly="surveyPublic.groupsOnly"
                    :users="surveyPublic.users"
                    :managersPermission="surveyAccess.managersPermission"
                    :surveyAccessUsers="surveyAccess.users"
                    :creator="summary.creator"
                    :editable="false"
                    @changeProperty="changeProperty"
                    @editSurveyPublic="editSurveyPublic"
                    @editSurveyAccessPermission="editSurveyAccessPermission"
                    @changeSurveyPublicProperty="changeSurveyPublicProperty"
                    @changeSurveyAccessProperty="changeSurveyAccessProperty"
                    @removeElementFromListOfObject="
                      removeElementFromListOfObject
                    "
                  />
                  <SurveyInformationsTab
                    v-if="activeTab === 2"
                    :languages="languages"
                    :availableLanguages="availableLanguages"
                    :selectedLanguage="selectedLanguage"
                    :questions="questions"
                    :title="title"
                    :description="description"
                    :categories="categories"
                    @changeLanguageProperty="changeLanguageProperty"
                    @addQuestion="addQuestion"
                    @removeQuestion="removeQuestion"
                    @addCategory="addCategory"
                    @removeCategory="removeCategory"
                    @changeLanguageListProperty="changeLanguageListProperty"
                    @changeListProperty="changeListProperty"
                    @moveQuestion="moveQuestion"
                    @moveCategory="moveCategory"
                    @createCategoryOfQuestion="createCategoryOfQuestion"
                    @addAnswer="addAnswer"
                    @removeAnswer="removeAnswer"
                    @togglePopoverAddCategory="togglePopoverAddCategory"
                    @changeLanguageListOfListProperty="
                      changeLanguageListOfListProperty
                    "
                    @changeListOfListProperty="changeListOfListProperty"
                    @removeQuestionFromCategory="removeQuestionFromCategory"
                    @moveUpQuestionAnswer="moveUpQuestionAnswer"
                    @moveDownQuestionAnswer="moveDownQuestionAnswer"
                  />
                  <EditFooter
                    v-if="status !== 'closed' && $can('create', 'customSurvey')"
                    :activeTab="activeTab"
                    :isLoading="isLoading"
                    @changeTab="changeTab"
                    @saveDraftAndExit="saveDraft(false)"
                  />
                  <EditSurveyLaunchModal
                    ref="survey-launch-modal"
                    :isNextDeliverySurveyDate="isNextDeliverySurveyDate"
                    :launchConfigurationReference="launchConfiguration"
                    @launchSurvey="launchSurvey"
                  />
                  <SurveyPublicModal
                    ref="survey-public-modal"
                    :sendToCompany="surveyPublic.sendToCompany"
                    :groupsWithHierarchy="surveyPublic.groupsWithHierarchy"
                    :groupsWithHierarchyOptions="
                      surveyPublic.groupsWithHierarchyOptions
                    "
                    :groupsOnly="surveyPublic.groupsOnly"
                    :groupsOnlyOptions="surveyPublic.groupsOnlyOptions"
                    :users="surveyPublic.users"
                    :usersOptions="surveyPublic.usersOptions"
                    @changeSurveyPublicProperty="changeSurveyPublicProperty"
                    @searchForUsers="searchForUsers"
                    @searchForGroups="searchForGroups"
                  />
                  <SurveyAccessPermissionModal
                    ref="survey-access-permission-modal"
                    :managersPermission="surveyAccess.managersPermission"
                    :users="surveyAccess.users"
                    :usersOptions="surveyAccess.usersOptions"
                    @changeSurveyAccessProperty="changeSurveyAccessProperty"
                    @searchForManagers="searchForManagers"
                  />
                </div>
                <UnauthorizedModal ref="modal-unauthorized" />
                <SurveyFormErrorsModal
                  ref="survey-form-errors-modal"
                  :errors="this.activeTabErrors"
                />
              </template>
            </PageLayout>
          </template>
        </PageLayout>
      </div>
      <ReportsSidebar v-if="false" />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { isAdmin } from '@/service/user'
import PageLayout from '@/components/Home/PageLayout.vue'
import EditMainNavbar from '@/components/CustomSurveys/Create/Navbars/EditMainNavbar.vue'
import LanguageNavbar from '@/components/CustomSurveys/Create/Navbars/LanguageNavbar.vue'
import EditFooter from '@/components/CustomSurveys/Create/Layout/EditFooter.vue'
import ConfigurationsTab from '@/components/CustomSurveys/Create/Tabs/ConfigurationsTab.vue'
import SurveyInformationsTab from '@/components/CustomSurveys/Create/Tabs/SurveyInformationsTab.vue'
import SummaryTab from '@/components/CustomSurveys/Create/Tabs/SummaryTab.vue'
import EditSurveyLaunchModal from '@/components/CustomSurveys/Create/Modals/EditSurveyLaunchModal.vue'
import SurveyPublicModal from '@/components/CustomSurveys/Create/Modals/SurveyPublicModal.vue'
import SurveyAccessPermissionModal from '@/components/CustomSurveys/Create/Modals/SurveyAccessPermissionModal.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import ReportsNavbar from '@/components/Home/CustomSurveys/ReportsNavbar.vue'
import ReportsSidebar from '@/components/Home/CustomSurveys/ReportsSidebar.vue'
import { HIGH } from '@/helpers/messages-priority'
import UnauthorizedModal from '@/components/CustomSurveys/Reports/UnauthorizedModal.vue'
import SurveyFormErrorsModal from '@/components/CustomSurveys/Create/Modals/SurveyFormErrorsModal.vue'

export default {
  components: {
    PageLayout,
    EditMainNavbar,
    LanguageNavbar,
    EditFooter,
    ConfigurationsTab,
    SurveyInformationsTab,
    SummaryTab,
    EditSurveyLaunchModal,
    SurveyPublicModal,
    SurveyAccessPermissionModal,
    Breadcrumb,
    ReportsNavbar,
    ReportsSidebar,
    UnauthorizedModal,
    SurveyFormErrorsModal,
  },
  data() {
    return {
      activeTab: 0,
      selectedLanguage: 0,
      isLoading: false,
      isNextDeliverySurveyDate: false,
      activeTabErrors: [],
    }
  },
  computed: {
    ...mapGetters({
      summary: 'customSurveyResults/summary',
      statusUpdate: 'customSurveys/statusUpdate',
      cycle: 'customSurveys/cycle',
      nextDeliveySurveyDate: 'customSurveys/nextDeliveySurveyDate',
      launchConfiguration: 'customSurveys/launchConfiguration',
      status: 'customSurveys/status',
      languages: 'languages',
      languageSelected: 'languageSelected',
      resources: 'resources',
      period: 'customSurveys/period',
      mandatoryQuestions: 'customSurveys/mandatoryQuestions',
      enableFeedbacks: 'customSurveys/enableFeedbacks',
      questionsDistribution: 'customSurveys/questionsDistribution',
      questionsQuantity: 'customSurveys/questionsQuantity',
      availableLanguages: 'customSurveys/availableLanguages',
      questions: 'customSurveys/questions',
      title: 'customSurveys/title',
      description: 'customSurveys/description',
      categories: 'customSurveys/categories',
      id: 'customSurveys/id',
      surveyPublic: 'customSurveys/surveyPublic',
      surveyAccess: 'customSurveys/surveyAccess',
    }),
    labels: function() {
      return {
        ...this.resources.component.customSurveys.create,
        ...this.resources.page.home.customSurveys,
      }
    },
    breadcrumbList() {
      return [
        {
          label: this.labels.title,
          link: { name: 'custom-surveys' },
        },
      ]
    },
    customSurveyId: function() {
      return this.$route.params.id
    },
    isAdmin() {
      return isAdmin()
    },
  },
  async mounted() {
    this.$root.$emit('setPageTitle', {
      context: this,
      path: 'custom-survey/settings',
    })
    this.$store.dispatch('customSurveys/resetState')
    await this.loadCustomSurvey()
  },
  methods: {
    async loadCustomSurvey() {
      const id = this.$route.params.id

      const requests = []
      requests.push(
        this.$store.dispatch('customSurveyResults/loadSummary', {
          customSurveyId: this.customSurveyId,
        })
      )
      requests.push(this.$store.dispatch('customSurveys/loadCustomSurvey', id))

      this.isLoading = true
      try {
        await Promise.all(requests)
      } catch (error) {
        const { status } = error.response
        if (status === 401) {
          this.$refs['modal-unauthorized'].show()
        }
      }
      this.isLoading = false
    },
    removeElementFromListOfObject({ object, property, value }) {
      this.$store.dispatch('customSurveys/removeElementFromListOfObject', {
        object,
        property,
        value,
      })
    },
    removeQuestionFromCategory({ categoryId, questionId }) {
      this.$store.dispatch('customSurveys/removeQuestionFromCategory', {
        categoryId,
        questionId,
      })
    },
    searchForGroups(value, property) {
      this.$store.dispatch('customSurveys/searchForGroups', { value, property })
    },
    searchForUsers(value) {
      this.$store.dispatch('customSurveys/searchForUsers', { value })
    },
    searchForManagers(value) {
      this.$store.dispatch('customSurveys/searchForUsers', {
        value,
        role: 'manager',
      })
    },
    exit() {
      this.$router.push({ name: 'custom-surveys' })
    },
    changeTab(tab) {
      this.activeTab = tab
    },
    selectLanguage(language) {
      this.selectedLanguage = language
    },
    changeProperty({ property, value }) {
      this.$store.dispatch('customSurveys/changeProperty', { property, value })
    },
    changeSurveyPublicProperty({ property, value }) {
      this.$store.dispatch('customSurveys/changeSurveyPublicProperty', {
        property,
        value,
      })
    },
    changeSurveyAccessProperty({ property, value }) {
      this.$store.dispatch('customSurveys/changeSurveyAccessProperty', {
        property,
        value,
      })
    },
    changeLanguageProperty({ property, language, value }) {
      this.$store.dispatch('customSurveys/changeLanguageProperty', {
        property,
        language,
        value,
      })
    },
    addQuestion() {
      this.$store.dispatch('customSurveys/addQuestion')
    },
    removeQuestion(idx) {
      this.$store.dispatch('customSurveys/removeQuestion', { idx })
    },
    changeLanguageListProperty({
      value,
      collection,
      index,
      property,
      language,
    }) {
      this.$store.dispatch('customSurveys/changeLanguageListProperty', {
        value,
        collection,
        index,
        property,
        language,
      })
    },
    changeListProperty({ value, collection, index, property }) {
      this.$store.dispatch('customSurveys/changeListProperty', {
        value,
        collection,
        index,
        property,
      })
    },
    addCategory() {
      this.$store.dispatch('customSurveys/addCategory')
    },
    removeCategory(idx) {
      this.$store.dispatch('customSurveys/removeCategory', { idx })
    },
    moveQuestion({ from, to }) {
      this.$store.dispatch('customSurveys/moveQuestion', { from, to })
    },
    moveCategory({ from, to }) {
      this.$store.dispatch('customSurveys/moveCategory', { from, to })
    },
    createCategoryOfQuestion({ questionIndex, categoryLabel }) {
      this.$store.dispatch('customSurveys/createCategoryOfQuestion', {
        questionIndex,
        categoryLabel,
        language: this.availableLanguages[this.selectedLanguage],
      })
    },
    launchSurvey(launchConfiguration) {
      this.$refs['survey-launch-modal'].hide()
      this.saveDraft(true, launchConfiguration)
    },
    async saveDraft(exit, launchConfiguration = {}) {
      const errors = [
        ...this.getConfigurationsTabErrors(),
        ...this.getSurveyInformationsTabErrors(),
      ]
      if (errors.length > 0) {
        this.activeTabErrors = errors
        return this.$refs['survey-form-errors-modal'].show()
      }

      // no errors
      this.activeTabErrors = []

      this.isLoading = true

      const onSaveSuccess = async () => {
        this.$root.$emit('app:message:show', {
          priority: HIGH,
          text: this.labels.saveSuccess,
          status: 'success',
        })
        if (exit) {
          await this.loadCustomSurvey()
        }
      }
      const onSaveError = () => {
        this.$root.$emit('app:message:show', {
          priority: HIGH,
          text: this.labels.saveError,
          status: 'danger',
        })
      }

      const {
        id,
        period,
        questionsDistribution,
        questionsQuantity,
        mandatoryQuestions,
        enableFeedbacks,
        availableLanguages,
        title,
        description,
        categories,
        questions,
        surveyPublic,
        surveyAccess,
      } = this

      const status = this.status

      const draft = {
        id,
        period,
        questionsDistribution,
        questionsQuantity,
        mandatoryQuestions,
        enableFeedbacks,
        availableLanguages,
        launchConfiguration,
        status,
        title,
        description,
        categories,
        questions: questions.map(
          ({ question_id, type, category, enableScore, text, answers }) => ({
            question_id,
            type,
            category,
            enableScore,
            text,
            answers,
          })
        ),
        surveyPublic: {
          sendToCompany: surveyPublic.sendToCompany,
          groupsWithHierarchy: surveyPublic.groupsWithHierarchy,
          groupsOnly: surveyPublic.groupsOnly,
          users: surveyPublic.users,
        },
        surveyAccess: {
          managersPermission: surveyAccess.managersPermission,
          users: surveyAccess.users,
        },
      }

      this.$store
        .dispatch('customSurveys/saveDraft', draft)
        .then(onSaveSuccess)
        .catch(onSaveError)
        .finally(() => {
          this.isLoading = false
        })
    },
    openChangeModal(isNextDeliverySurveyDate) {
      this.isNextDeliverySurveyDate = isNextDeliverySurveyDate
      const modal = this.$refs['survey-launch-modal']
      modal.clearState()
      modal.show()
    },
    editSurveyPublic() {
      this.$refs['survey-public-modal'].show()
    },
    editSurveyAccessPermission() {
      this.$refs['survey-access-permission-modal'].show()
    },
    addAnswer({ questionIndex }) {
      this.$store.dispatch('customSurveys/addAnswer', { questionIndex })
    },
    removeAnswer({ answerId, questionIndex }) {
      this.$store.dispatch('customSurveys/removeAnswer', {
        answerId,
        questionIndex,
      })
    },
    togglePopoverAddCategory({ index, value }) {
      this.$store.dispatch('customSurveys/togglePopoverAddCategory', {
        index,
        value,
      })
    },
    changeLanguageListOfListProperty({
      value,
      collection,
      index,
      listCollection,
      listIndex,
      property,
      language,
    }) {
      this.$store.dispatch('customSurveys/changeLanguageListOfListProperty', {
        value,
        collection,
        index,
        listCollection,
        listIndex,
        property,
        language,
      })
    },
    changeListOfListProperty({
      value,
      collection,
      index,
      listCollection,
      listIndex,
      property,
    }) {
      this.$store.dispatch('customSurveys/changeListOfListProperty', {
        value,
        collection,
        index,
        listCollection,
        listIndex,
        property,
      })
    },
    moveUpQuestionAnswer({ qIdx, index }) {
      this.$store.dispatch('customSurveys/moveUpQuestionAnswer', {
        qIdx,
        index,
      })
    },
    moveDownQuestionAnswer({ qIdx, index }) {
      this.$store.dispatch('customSurveys/moveDownQuestionAnswer', {
        qIdx,
        index,
      })
    },
    getConfigurationsTabErrors() {
      const errors = []
      // check survey period
      if (this.period === '') {
        errors.push({
          field: this.labels.tabs.period.label,
          message: this.labels.tabs.period.select,
        })
      }
      // check survey available languages
      if (this.availableLanguages.length === 0) {
        errors.push({
          field: this.labels.tabs.languages.label,
          message: this.labels.tabs.languages.select,
        })
      }
      // check survey questions distribution
      if (this.questionsDistribution === '') {
        errors.push({
          field: this.labels.tabs.questionsDistribution.label,
          message: this.labels.tabs.questionsDistribution.select,
        })
      }
      // check survey questions quantity
      if (this.period !== 'single-shot' && this.questionsQuantity === 0) {
        errors.push({
          field: this.labels.tabs.questionsQuantity.label,
          message: this.labels.tabs.questionsQuantity.select,
        })
      }
      // check survey public
      if (
        !this.surveyPublic.sendToCompany &&
        this.surveyPublic.groupsWithHierarchy.length +
          this.surveyPublic.groupsOnly.length +
          this.surveyPublic.users.length ===
          0
      ) {
        errors.push({
          field: this.labels.tabs.public.label,
          message: this.labels.tabs.public.select,
        })
      }
      return errors
    },
    getSurveyInformationsTabErrors() {
      const errors = []
      // check survey questions quantity
      if (this.questions.length === 0) {
        errors.push({
          field: this.labels.tabs.questions,
          message: this.labels.questions.select,
        })
      } else {
        this.questions.forEach((question, index) => {
          if (
            question.type !== 'feedback' &&
            [null, undefined].includes(question.enableScore)
          ) {
            errors.push({
              field: `${index + 1}° ${this.labels.questions.question}`,
              message: `${this.labels.questions.score.title}`,
            })
          }
          if (
            ['select', 'multi-select'].includes(question.type) &&
            (!question.answers || question.answers.length === 0)
          ) {
            errors.push({
              field: `${index + 1}° ${this.labels.questions.question}`,
              message: `${this.labels.questions.answers.notFound}`,
            })
          }
        })
      }

      // check translations
      this.availableLanguages.forEach((language, languageIndex) => {
        const { idiom } = this.languages.find(l => l.code === language)
        // check title
        if ([null, undefined, ''].includes(this.title[language])) {
          errors.push({
            field: this.labels.tabs.surveyTitle.label,
            message: `${this.labels.tabs.surveyTitle.placeholder} (${idiom})`,
          })
        }
        // check questions
        if (this.questions.length > 0) {
          this.questions.forEach((question, index) => {
            if ([null, undefined, ''].includes(question.text[language])) {
              errors.push({
                field: `${this.labels.questions.text.title} ${index + 1}`,
                message: `${this.labels.questions.text.placeholder} (${idiom})`,
              })
            }
            if (
              ['select', 'multi-select'].includes(question.type) &&
              question.answers &&
              question.answers.length > 0
            ) {
              question.answers.forEach((answer, answerIndex) => {
                if (
                  languageIndex === 0 &&
                  question.enableScore &&
                  [null, undefined, ''].includes(answer.classification)
                ) {
                  errors.push({
                    field: `${index + 1}° ${this.labels.questions.question}`,
                    message: `${
                      this.labels.questions.answers.type.missingTitle
                    } ${answerIndex + 1}° ${
                      this.labels.questions.answers.singleTitle
                    }`,
                  })
                }
                if ([null, undefined, ''].includes(answer.text[language])) {
                  errors.push({
                    field: `${index + 1}° ${this.labels.questions.question}`,
                    message: `${answerIndex + 1}° ${
                      this.labels.questions.answers.singleTitle
                    } (${idiom})`,
                  })
                }
              })
            }
          })
        }
        // check categories
        if (this.categories.length > 0) {
          this.categories.forEach((category, index) => {
            if ([null, undefined, ''].includes(category.label[language])) {
              errors.push({
                field: `${this.labels.categories.name.title} ${index + 1}`,
                message: `${this.labels.categories.name.placeholder} (${idiom})`,
              })
            }
          })
        }
      })
      return errors
    },
  },
}
</script>

<style lang="scss">
@import '~@/styles/custom/colors';
.custom-surveys-edit-page {
  header {
    margin-top: 0 !important;
  }
  .card {
    box-shadow: none;
    border-radius: 10px !important;

    .card-header {
      border-top-left-radius: 10px !important;
      border-top-right-radius: 10px !important;
    }

    .v-select .vs__dropdown-menu,
    .v-select .vs__dropdown-toggle .vs__selected {
      border-radius: 10px !important;
    }

    .v-select .vs__dropdown-toggle,
    .custom-survey-form-input {
      border-radius: 10px !important;
      background-color: map-get($theme-colors, 'white') !important;
      border-color: var(--gray07) !important;
    }

    .readonly-input .vs__dropdown-toggle {
      border-color: map-get($theme-colors, 'white') !important;
    }
  }
}
</style>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.custom-surveys-configuration-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  width: 100%;

  .body {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    overflow: hidden;

    .content {
      padding: 0;
      position: relative;
      z-index: 0;
      min-width: 0px;
      flex: 1 1 auto;
      overflow: auto;
      flex-grow: 1;
      --webkit-flex-grow: 1;

      header {
        .d-flex {
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
      }

      .custom-surveys-edit-page {
        padding-left: 0 !important;
        padding-right: 0 !important;

        header {
          .header-wrap {
            width: 100%;
            display: flex;
            flex-direction: column;

            h1 {
              text-align: center;
              color: map-get($theme-colors, 'secondary');
              font-size: 1.7rem;
            }
          }
        }

        .survey-card {
          .apply-form-group-btn {
            font-size: 1.5rem;
            border-radius: 10px !important;
            min-width: 50px;
          }
          .readonly-input {
            background-color: map-get($theme-colors, 'white') !important;
            border-color: map-get($theme-colors, 'white') !important;
            color: #6c757d !important;
            height: 42px !important;
          }
          .custom-survey-status {
            &.scheduled {
              color: map-get($theme-colors, 'purple00') !important;
            }
            &.active {
              color: map-get($theme-colors, 'green-dark') !important;
            }
            &.closed {
              color: map-get($theme-colors, 'secondary') !important;
            }
          }
        }

        .custom-surveys-create-container {
          display: flex;
          width: 100%;
          flex-direction: column;

          .content {
            min-height: 100%;
            width: 100%;
            height: 100%;
            overflow-y: auto;

            /deep/ .form-group {
              label:not(.custom-input-label),
              legend {
                color: map-get($theme-colors, 'primary') !important;
                position: absolute;
                top: -18px;
                margin-left: 20px;
                font-size: 12px;
              }
              small {
                font-size: 12px;
                margin-left: 15px;
              }
            }
          }
        }
      }
    }
  }
}
</style>
